import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import AboutOverview from './AboutOverview';
import AboutMission from './AboutMission';
import AboutService from './AboutService';
import OrganizationSt from './OrganizationSt';
import Staff from './Staff';
import Centers from './centers';
import Infor from './Infor';
import CallCenter from './CallCenter';
import AgriPaper from './AgriPaper';
import AthPatrika from './AthPatrika';
import Govigivisum from './Govigivisum';
import Rajawaka from './Rajawaka';
import FarmProducts from './FarmProducts';
import AdminPanel from './Admin/AdminPanel';
import FAQ from './FAQ';
import Crops from './Crops';
import Wee from './Crops/wee';
import GenericCropPage from './Crops/GenericCropPage';
import InfoColumns from './InfoColumns';
import Footer from './Footer';
import DynamicCenterView from './components/DynamicCenterView';
import Procurement from './Procurement';
import { getApiUrl, getDepartmentDbUrl } from './apiConfig';

const slides = ['/banner.jpg', '/banner2.jpg', '/banner3.jpg'];

function App() {
  // Professional Analytics: track daily views on server
  useEffect(() => {
    if (localStorage.getItem('admin_logged_in') === 'true') return;
    if (window.viewCounted) return;
    window.viewCounted = true;

    // Do not fire POST on development port 3000 to prevent Webpack Dev Server file watcher reloads
    if (window.location.port && window.location.port !== '80' && window.location.port !== '443') {
      return;
    }

    const url = getApiUrl('api_visuals.php');

    fetch(url, { method: 'POST' })
      .then(r => r.json())
      .catch(e => console.error("Analytics sync failed", e));
  }, []);

  const [activeTab, setActiveTab] = useState(() => {
    try {
      const qp = new URLSearchParams(window.location.search);
      // Support ?admin, ?admin=true, ?page=admin, or #admin
      if (qp.has('admin') || qp.get('page') === 'admin' || window.location.hash === '#admin') {
        return 'admin';
      }
      const page = qp.get('page');
      if (page) return page;
    } catch (e) { }
    return 'home';
  });

  const prevTabRef = useRef(localStorage.getItem('prev_active_tab') || 'home');

  useEffect(() => {
    if (activeTab !== 'admin') {
      prevTabRef.current = activeTab;
      localStorage.setItem('prev_active_tab', activeTab);
    }
  }, [activeTab]);

  // Support browser back/forward buttons and direct URL navigation
  useEffect(() => {
    const handlePopState = () => {
      try {
        const qp = new URLSearchParams(window.location.search);
        if (qp.has('admin') || qp.get('page') === 'admin' || window.location.hash === '#admin') {
          setActiveTab('admin');
        } else {
          setActiveTab(qp.get('page') || 'home');
        }
      } catch (e) { }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Secret shortcut: Ctrl + Shift + A to open Admin Login from anywhere
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
        e.preventDefault();
        localStorage.removeItem('admin_logged_in');
        changeTab('admin');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeTab = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    window.scrollTo(0, 0);
    try {
      let url = window.location.pathname;
      if (tab === 'admin') {
        url += '?admin';
      } else if (tab !== 'home') {
        url += '?page=' + tab;
      }
      window.history.pushState({}, '', url);
    } catch (e) { }
    // Close mobile menu on navigate
    setShowMobileMenu(false);
    setShowAboutMenu(false);
    setShowInformationMenu(false);
  };

  const handleAdminExit = () => {
    const backTab = prevTabRef.current && prevTabRef.current !== 'admin' ? prevTabRef.current : 'home';
    changeTab(backTab);
  };

  const [showAboutMenu, setShowAboutMenu] = useState(false);
  const [showInformationMenu, setShowInformationMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const nextSlide = () => setCurrentSlide(s => (s + 1) % slides.length);
  const prevSlide = () => setCurrentSlide(s => (s - 1 + slides.length) % slides.length);

  // Auto-advance banner slides every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(s => (s + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const isSyncingRef = useRef(false);

  const fetchNewsData = async (isCancelled = () => false) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;

    const url = getApiUrl(`api_news.php?t=${Date.now()}`);

    try {
      const r = await fetch(url, {
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();

      if (isCancelled()) return;

      let list = Array.isArray(data) ? data : (data?.headlines || []);

      // 🛑 CRITICAL: Ensure newest news are always first
      if (list.length > 0) {
        list = [...list].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
      }

      if (list && Array.isArray(list)) {
        const newDataStr = JSON.stringify(list);
        setNewsItems(prevItems => {
          if (JSON.stringify(prevItems) !== newDataStr) {
            console.log('🔄 News update: ' + list.length + ' items');
            localStorage.setItem('site_news', newDataStr);
            return list;
          }
          return prevItems;
        });
      }
    } catch (err) {
      console.error("News fetch error:", err);
    } finally {
      isSyncingRef.current = false;
    }
  };

  useEffect(() => {
    let isCancelled = false;
    fetchNewsData(() => isCancelled);
    return () => {
      isCancelled = true;
    };
  }, []);

  const [newsItems, setNewsItems] = useState(() => {
    try {
      const local = localStorage.getItem('site_news');
      if (!local) return [];
      const data = JSON.parse(local);
      const list = Array.isArray(data) ? data : (data?.headlines || []);
      return [...list].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
    } catch (e) {
      console.error("Initial news load error:", e);
      return [];
    }
  });
  const [selectedNews, setSelectedNews] = useState(null);
  const [showNewsModal, setShowNewsModal] = useState(false);
  const [showAllNews, setShowAllNews] = useState(false);



  const openNewsModal = (newsItem) => {
    setSelectedNews(newsItem);
    setShowNewsModal(true);
  };

  const closeNewsModal = () => {
    setShowNewsModal(false);
    setSelectedNews(null);
  };



  const maps = [
    { id: 'map1', label: 'බෝග නිෂ්පාදනය' },
    { id: 'map3', label: 'රජවක නවාතැන් පහසුකම්' },
    { id: 'map5', label: 'ගොවිපළ නිෂ්පාදන' },
  ];

  const mainPageExclusions = [
    'about-overview', 'about-mission', 'about-service', 'OrganizationSt', 'about-staff',
    'organization', 'call-center', 'rathnpura-deputy', 'rathnpura-assistant',
    'rathnpura-nivithigala', 'rathnpura-balangoda', 'rathnpura-embilipitiya',
    'service-center', 'rathnpura-karapincha', 'rathnpura-tranee-embilipitiya',
    'kegalla-deputy', 'kegalla-assistant', 'kegalla-ruwanwella', 'kegalla-mawanella',
    'TraneeWagolla', 'CentralPlants', 'Marukwathura', 'Bimmal', 'Panshu', 'PatakaRopana',
    'tirasara-pro', 'agripaper', 'athpatrika', 'Govigivisum', 'rajawaka', 'farmproducts',
    'admin', 'faq', 'infor', 'crops', 'wee', 'procurement'
  ];

  const showMainContent = activeTab === 'home' || (!mainPageExclusions.includes(activeTab) && !activeTab.startsWith('crop-'));

  return (
    <div className="App">
      {activeTab !== 'admin' && (
        <div className={`site-header ${scrolled ? 'scrolled' : ''}`}>
          <div className="top-bar">
            <div className="top-bar-inner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
              <button className="mobile-menu-toggle" onClick={() => setShowMobileMenu(!showMobileMenu)}>{showMobileMenu ? '✕' : '☰'}</button>
              <div className="contact-left">
                <span className="phone">📞 0452222356</span>
                <span className="email">✉️ agrisabara@gmail.com</span>
              </div>
              <div className="lang-right" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <a href="#!" onClick={(e) => e.preventDefault()} style={{ color: 'white', textDecoration: 'none', fontSize: '13px' }}>සිංහල</a>
                <a href="#!" onClick={(e) => e.preventDefault()} style={{ color: 'white', textDecoration: 'none', fontSize: '13px' }}>தமிழ்</a>
                <a href="#!" onClick={(e) => e.preventDefault()} style={{ color: 'white', textDecoration: 'none', fontSize: '13px' }}>English</a>
              </div>
            </div>
          </div>

          <header className="main-header">
            <div className="header-inner" onClick={() => changeTab('home')} style={{ cursor: 'pointer' }}>
              <div className="logo-left"><img src="/logo01.png" alt="logo" /></div>
              <div className="title-wrap">
                <div className="line sinhala">සබරගමුව පළාත් කෘෂිකර්ම දෙපාර්තමේන්තුව</div>
                <div className="line tamil">சபரகமுவ மாகாண விவசாயத் துறை</div>
                <div className="line english">Department of Agriculture Sabaragamuwa Province</div>
              </div>
              <div className="logos-right">
                <img src="/flag.png" alt="flag" className="flag-image" />
                <img src="/logo02.png" alt="logo" />
              </div>
            </div>
          </header>

          <nav className="nav-tabs">
            <ul className={"nav-list" + (showMobileMenu ? ' open' : '')}>
              <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('home'); }} className={activeTab === 'home' ? 'active' : ''}><span>මුල් පිටුව</span></a></li>
              <li>
                <a href="#!" onClick={(e) => { e.preventDefault(); setShowAboutMenu(!showAboutMenu); setShowInformationMenu(false); }} className={activeTab.includes('about') || activeTab === 'OrganizationSt' ? 'active' : ''}><span>අප ගැන</span></a>
                <ul className={"submenu" + (showAboutMenu ? ' open' : '')}>
                  <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('about-overview'); }}>හැදින්වීම</a></li>
                  <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('about-mission'); }}>කාර්‍යභාරය</a></li>
                  <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('about-service'); }}>අපගේ සේවාවන්</a></li>
                  <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('OrganizationSt'); }}>සංවිධාන ව්‍යුහය</a></li>
                  <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('about-staff'); }}>කාර්ය මණ්ඩලය</a></li>
                </ul>
              </li>
              <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('organization'); }} className={activeTab === 'organization' ? 'active' : ''}><span>ආයතන හා මධ්‍යස්ථාන</span></a></li>
              <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('projects'); }} className={activeTab === 'projects' ? 'active' : ''}><span>ව්‍යාපෘතීන්</span></a></li>
              <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('procurement'); }} className={activeTab === 'procurement' ? 'active' : ''}><span>ප්‍රසම්පාදන</span></a></li>
              <li>
                <a href="#!" onClick={(e) => { e.preventDefault(); setShowInformationMenu(!showInformationMenu); setShowAboutMenu(false); }} className={activeTab === 'infor' ? 'active' : ''}><span>තො.දැ.ගැ.පනත</span></a>
                <ul className={"submenu" + (showInformationMenu ? ' open' : '')}>
                  <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('infor'); }}>තොරතුරු දැනගැනීමේ නිලධාරීන්</a></li>
                </ul>
              </li>
              <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('faq'); }} className={activeTab === 'faq' ? 'active' : ''}><span>පොදු ප්‍රශ්න</span></a></li>
              <li><a href="#!" onClick={(e) => { e.preventDefault(); changeTab('call-center'); }} className={activeTab === 'call-center' ? 'active' : ''}><span>අප අමතන්න</span></a></li>
            </ul>
          </nav>
        </div>
      )}

      {showMainContent && (
        <>
          <section className="hero">
            {slides.map((src, i) => (
              <div key={src + i} className={"hero-slide" + (i === currentSlide ? ' active' : '')} style={{ backgroundImage: `url(${src})` }} />
            ))}
            <button className="nav-arrow left" onClick={prevSlide}>‹</button>
            <button className="nav-arrow right" onClick={nextSlide}>›</button>
          </section>

          <div className="news-boxes-container">
            <div className="news-boxes-inner">
              <div
                className="latest-news-label"
                onClick={() => fetchNewsData()}
                style={{ cursor: 'pointer' }}
                title="Click to Refresh"
              >
                Latest News ↻
              </div>
              <div className="latest-news-display-grid">
                {(showAllNews ? newsItems : newsItems.slice(0, 4)).map((item, idx) => (
                  <div key={item.id || `news-${idx}`} className="news-box-item">
                    <div className="news-box-image">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.title || "news"}
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div className="news-placeholder" style={{ display: item.image ? 'none' : 'flex' }}>
                        {item.title ? item.title.substring(0, 10) : `News`}
                      </div>
                    </div>
                    <div className="news-box-content">
                      <h4 className="news-box-title">{item.title || "Latest Update"}</h4>
                      <p className="news-box-summary">{item.summary ? (item.summary.length > 60 ? item.summary.substring(0, 60) + "..." : item.summary) : "Click to read more..."}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '5px' }}>
                        <span className="news-box-date">{item.published_at || "2024-05-13"}</span>
                        {item.title && <button onClick={() => openNewsModal(item)} className="view-details-btn">More Details</button>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {newsItems.length > 4 && (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                <button
                  className="view-details-btn"
                  style={{ padding: '10px 24px', fontSize: '14px' }}
                  onClick={() => setShowAllNews(!showAllNews)}
                >
                  {showAllNews ? 'Show Less News' : 'View All News'}
                </button>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', margin: '14px 0' }}>
              <button className="go-to-db-btn" onClick={() => window.open(getDepartmentDbUrl(), '_blank')}>
                🏛️ Department Database
              </button>
            </div>
          </div>

          <section className="card-grid-section">
            <div className="card-grid-inner">
              {maps.map(m => (
                <button key={m.id} className="design-card small" onClick={() => {
                  if (m.id === 'map1') changeTab('crops');
                  else if (m.id === 'map2') changeTab('agripaper');
                  else if (m.id === 'map3') changeTab('rajawaka');
                  else if (m.id === 'map5') changeTab('farmproducts');
                }}>
                  <div className="envelope"><div className="envelope-body"><div className="envelope-inner" /></div><div className="envelope-flap" /></div>
                  <div className="feature-text">{m.label}</div>
                </button>
              ))}
            </div>
          </section>

          <InfoColumns />
        </>
      )}

      {showNewsModal && selectedNews && (
        <div className="modal-overlay" onClick={closeNewsModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="close-modal" onClick={closeNewsModal}>×</button>
            {selectedNews.image && <img src={selectedNews.image} alt="news" className="modal-image" />}
            <div style={{ padding: '25px' }}>
              <h2>{selectedNews.title}</h2>
              <p className="modal-date">📅 {selectedNews.published_at}</p>
              <p className="modal-text">{selectedNews.summary || 'No details available.'}</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'about-overview' && <AboutOverview onBackClick={() => changeTab('home')} />}
      {activeTab === 'about-mission' && <AboutMission onBackClick={() => changeTab('home')} />}
      {activeTab === 'about-service' && <AboutService onBackClick={() => changeTab('home')} />}
      {activeTab === 'OrganizationSt' && <OrganizationSt onBackClick={() => changeTab('home')} />}
      {activeTab === 'about-staff' && <Staff onBackClick={changeTab} />}
      {activeTab === 'organization' && <Centers onBackClick={changeTab} />}
      {activeTab === 'call-center' && <CallCenter onBackClick={() => changeTab('home')} />}
      {/* Dynamic Center Pages - Fully editable from Admin Panel */}
      {([
        'rathnpura-deputy', 'rathnpura-assistant', 'rathnpura-nivithigala', 'rathnpura-balangoda',
        'rathnpura-embilipitiya', 'service-center', 'rathnpura-karapincha', 'rathnpura-tranee-embilipitiya',
        'kegalla-deputy', 'kegalla-assistant', 'kegalla-ruwanwella', 'kegalla-mawanella',
        'TraneeWagolla', 'CentralPlants', 'Marukwathura', 'Bimmal', 'Panshu', 'PatakaRopana',
        'tirasara-pro'
      ].includes(activeTab) || activeTab.startsWith('center-')) && (
        <DynamicCenterView slug={activeTab} onBackClick={() => changeTab('organization')} />
      )}
      {activeTab === 'agripaper' && <AgriPaper onCardClick={changeTab} />}
      {activeTab === 'athpatrika' && <AthPatrika onBackClick={() => changeTab('agripaper')} />}
      {activeTab === 'Govigivisum' && <Govigivisum onBackClick={() => changeTab('agripaper')} />}
      {activeTab === 'rajawaka' && <Rajawaka onBackClick={() => changeTab('home')} />}
      {activeTab === 'farmproducts' && <FarmProducts onBackClick={() => changeTab('home')} />}
      {activeTab === 'admin' && <AdminPanel onBack={handleAdminExit} />}
      {activeTab === 'faq' && <FAQ onBackClick={() => changeTab('home')} />}
      {activeTab === 'infor' && <Infor onBackClick={() => changeTab('home')} />}
      {activeTab === 'crops' && <Crops onBackClick={() => changeTab('home')} onNavigate={changeTab} />}
      {activeTab === 'wee' && <Wee onBackClick={() => changeTab('crops')} />}
      {activeTab === 'crop-vegetables' && <GenericCropPage category="vegetables" title="එළවළු වගාව" onBackClick={() => changeTab('crops')} />}
      {activeTab === 'crop-fruits' && <GenericCropPage category="fruits" title="පලතුරු වගාව" onBackClick={() => changeTab('crops')} />}
      {activeTab === 'crop-ofc' && <GenericCropPage category="ofc" title="අතිරේක ආහාර බෝග" onBackClick={() => changeTab('crops')} />}
      {activeTab === 'crop-spices' && <GenericCropPage category="spices" title="කුළුබඩු බෝග" onBackClick={() => changeTab('crops')} />}
      {activeTab === 'crop-roots' && <GenericCropPage category="roots" title="අල බෝග" onBackClick={() => changeTab('crops')} />}
      {activeTab === 'procurement' && <Procurement onBackClick={() => changeTab('home')} />}

      {activeTab !== 'admin' && <Footer />}
    </div>
  );
}

export default App;
